import * as React from "react";
import { CookiesProvider } from "react-cookie";
import "semantic-ui-css/semantic.min.css";
import "./styling/base.scss";
import "./styling/tailwind.css";
import { EnvContext } from "./contexts/appContexts";
import { IntlProvider } from "react-intl";
import { ThemeProvider, theme } from "./styling/theme";
import { useLocation } from "react-router-dom";
import Routes from "./Routes";
import { EnvValues } from "./interfaces/customTypes";
import { useAnalyticsInitalize } from "./utils/gtag";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { get, set, del } from "idb-keyval";
import { ConfirmDialogProvider, SidebarProvider, Toaster, Tooltip } from "./components/ui";

interface Props {
  envValues: EnvValues;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24, // 24 hours (time to keep in memory, should be longer than staleTime)
    },
  },
});

const asyncPersister = createAsyncStoragePersister({
  storage: {
    getItem: async key => {
      const value = await get(key);
      return value ?? null;
    },
    setItem: async (key, value) => {
      await set(key, value);
    },
    removeItem: async key => {
      await del(key);
    },
  },
});

const App: React.FC<Props> = ({ envValues }): JSX.Element => {
  useAnalyticsInitalize(envValues.googleAnalyticsId);
  const { pathname } = useLocation();
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo(0, 0);
    }
  }, [pathname]);

  return (
    <CookiesProvider>
      <EnvContext.Provider value={envValues}>
        <IntlProvider locale="en">
          <ThemeProvider theme={theme}>
            <SidebarProvider>
              <PersistQueryClientProvider
                client={queryClient}
                persistOptions={{
                  persister: asyncPersister,
                  maxAge: 1000 * 60 * 60 * 12, // 12 hours (time to keep in disk)
                  dehydrateOptions: {
                    shouldDehydrateQuery: query => {
                      return query.meta?.persist === true;
                    },
                  },
                }}
              >
                <Tooltip.Provider>
                  <div className="flex flex-col h-dvh w-full">
                    <div ref={scrollContainerRef} className="grow min-h-0 overflow-y-auto overflow-x-hidden">
                      <Toaster />
                      <Routes {...envValues} />
                    </div>
                  </div>
                </Tooltip.Provider>
                <ConfirmDialogProvider />
              </PersistQueryClientProvider>
            </SidebarProvider>
          </ThemeProvider>
        </IntlProvider>
      </EnvContext.Provider>
    </CookiesProvider>
  );
};

export default App;
